import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
  type PropsWithChildren,
} from "react";

import {
  type FormikState,
  type FormikValues,
  type FormikConfig,
  useFormik,
  getIn,
} from "formik";

import { useObjectMemo } from "../../hooks";
import { useL10n, type TypeSafeReactLocalization } from "../../composites";

import type * as Yup from "yup";

type CreateFormConfig<Values extends FormikValues> = Omit<
  FormikConfig<Values>,
  "onSubmit" | "validationSchema"
> & {
  onSubmit?: FormikConfig<Values>["onSubmit"];
  translateValidationSchema?: (l10n: TypeSafeReactLocalization) => Yup.Schema;
  validationSchema?: Yup.Schema;
};

type SetValidationSchema = (
  schema: Yup.Schema | ((l10n: TypeSafeReactLocalization) => Yup.Schema),
) => void;

export function createForm<Values extends FormikValues>(
  defaultProps: CreateFormConfig<Values>,
) {
  type FormType = ReturnType<typeof useFormik<Values>>;
  const FormContext = createContext<FormType | undefined>(undefined);
  const FormValidationContext = createContext<{
    validationSchema: FormikConfig<Values>["validationSchema"];
    setValidationSchema: SetValidationSchema;
    resetValidationSchema: () => void;
  } | null>(null);

  type FormSubmission = { success: false } | { success: true; values: Values };

  const useForm = () => {
    const form = useContext(FormContext);
    if (!form) {
      throw new Error("useForm must be used within a FormProvider");
    }
    const formValidation = useContext(FormValidationContext);
    if (!formValidation) {
      throw new Error("useForm must be used within a FormValidationProvider");
    }
    const {
      initialValues,
      values,
      handleSubmit,
      validateForm,
      resetForm,
      setFieldValue,
    } = form;
    const { setValidationSchema, validationSchema, resetValidationSchema } =
      formValidation;

    const submitForm = useCallback<() => Promise<FormSubmission>>(async () => {
      handleSubmit();
      const errors = await validateForm(values);
      const success = Object.keys(errors).length === 0;
      if (success) {
        return { success, values };
      }
      return { success };
    }, [handleSubmit, validateForm, values]);

    const resetFormAndValidationSchema = useCallback(
      (nextState?: Partial<FormikState<Values>> | undefined) => {
        resetForm(nextState);
        resetValidationSchema();
      },
      [resetForm, resetValidationSchema],
    );

    const resetFieldToInitialValue = useCallback(
      (field: string) => {
        setFieldValue(field, getIn(initialValues, field));
      },
      [setFieldValue, initialValues],
    );

    const augmentedForm = useObjectMemo({
      ...form,
      submitForm,
      setValidationSchema,
      validationSchema,
      resetFormAndValidationSchema,
      resetFieldToInitialValue,
    });

    return augmentedForm;
  };

  interface ProviderProps extends PropsWithChildren {
    initialValues?: Values;
    onSubmit?: FormikConfig<Values>["onSubmit"];
    validateOnMount?: boolean;
    validateOnBlur?: boolean;
    validateOnChange?: boolean;
  }

  const Provider = ({
    children,
    onSubmit,
    initialValues,
    ...restProps
  }: ProviderProps) => {
    const l10n = useL10n();

    // Prefer the translated schema if available, reload on l10n change to update language
    const defaultSchema = useMemo(
      () =>
        defaultProps.translateValidationSchema?.(l10n) ??
        defaultProps.validationSchema,
      [l10n],
    );

    const [currentValidationSchema, setValidationSchemaInternal] =
      useState(defaultSchema);

    useEffect(() => {
      // on mount, set the validation schema to the default (to get the translated errors)
      setValidationSchemaInternal(defaultSchema);
      // we do not want to reset on any change to defaultSchema after mount,
      // as that would reset any dynamic changes to the schema
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const form = useFormik({
      ...defaultProps,
      ...restProps,
      initialValues: initialValues ?? defaultProps.initialValues,
      validationSchema: currentValidationSchema,
      onSubmit: onSubmit ?? defaultProps.onSubmit ?? (() => {}),
    });

    const resetValidationSchema = useCallback(() => {
      setValidationSchemaInternal(defaultSchema);
    }, [defaultSchema]);

    const setValidationSchema = useCallback<SetValidationSchema>(
      (schema) => {
        if (typeof schema === "function") {
          setValidationSchemaInternal(schema(l10n));
        } else {
          setValidationSchemaInternal(schema);
        }
      },
      [l10n],
    );

    const formValidationValue = useMemo(
      () => ({
        validationSchema: currentValidationSchema,
        setValidationSchema,
        resetValidationSchema,
      }),
      [currentValidationSchema, setValidationSchema, resetValidationSchema],
    );

    return (
      <FormValidationContext.Provider value={formValidationValue}>
        <FormContext.Provider value={form}>{children}</FormContext.Provider>
      </FormValidationContext.Provider>
    );
  };

  function Wrap<T extends object>(Component: React.ComponentType<T>) {
    return function WrappedComponent(props: T) {
      return (
        <Provider>
          <Component {...props} />
        </Provider>
      );
    };
  }

  return {
    Provider,
    useForm,
    Wrap,
  };
}
